import { cn } from "../lib/cn";
import {
  AxisLabels,
  CHART_TONES,
  ChartLegend,
  ChartTableFallback,
  ChartTooltip,
  MARK_FOCUS,
  SvgFrame,
  defaultFormat,
  niceMax,
  useChartTooltip,
  type ChartTone,
} from "./chart-base";

const W = 600;
const H = 220;
const GAP_RATIO = 0.25;

interface BaseBarProps {
  /** Accessible chart name (SVG <title>, table caption). */
  title: string;
  description?: string;
  /** Labels under the x-axis (a subset is fine, e.g. 4 dates). */
  axisLabels?: readonly string[];
  max?: number;
  formatValue?: (value: number) => string;
  /** Plot height utility, default `h-56`. */
  className?: string;
}

export interface BarDatum {
  label: string;
  value: number;
}

export interface BarChartProps extends BaseBarProps {
  data: readonly BarDatum[];
  tone?: ChartTone;
  seriesLabel?: string;
}

function slot(i: number, count: number) {
  const width = W / Math.max(count, 1);
  const gap = width * GAP_RATIO;
  return { x: i * width + gap / 2, width: width - gap, center: (i + 0.5) * width };
}

/** Single-series vertical bars; each bar is focusable with a tooltip. */
export function BarChart({ data, tone = "brand", title, description, axisLabels = [], max, formatValue = defaultFormat, seriesLabel = "Value", className }: BarChartProps) {
  const { tip, show, hide } = useChartTooltip();
  const top = max ?? niceMax(data.map((d) => d.value));
  return (
    <div>
      <div className={cn("relative", className ?? "h-56")}>
        <SvgFrame title={title} description={description} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full" onMouseLeave={hide}>
          {data.map((d, i) => {
            const s = slot(i, data.length);
            const h = (Math.max(d.value, 0) / top) * H;
            const text = `${d.label}: ${formatValue(d.value)}`;
            const reveal = () => {
              show({ x: (s.center / W) * 100, y: ((H - h) / H) * 100, text, index: i });
            };
            return (
              <rect
                key={d.label}
                x={s.x}
                y={H - h}
                width={s.width}
                height={h}
                rx={3}
                tabIndex={0}
                role="img"
                aria-label={text}
                vectorEffect="non-scaling-stroke"
                className={cn(CHART_TONES[tone].fill, MARK_FOCUS, "hover:opacity-80", tip?.index === i && "opacity-80")}
                onMouseEnter={reveal}
                onFocus={reveal}
                onBlur={hide}
              />
            );
          })}
        </SvgFrame>
        <ChartTooltip tip={tip} />
      </div>
      <AxisLabels labels={axisLabels} />
      <ChartTableFallback caption={title} table={{ columns: ["", seriesLabel], rows: data.map((d) => [d.label, formatValue(d.value)]) }} />
    </div>
  );
}

export interface StackSeries {
  key: string;
  label: string;
  tone: ChartTone;
}

export interface StackedDatum {
  label: string;
  /** Values keyed by series key; series[0] sits at the bottom. */
  values: Readonly<Record<string, number>>;
}

export interface StackedBarChartProps extends BaseBarProps {
  series: readonly StackSeries[];
  data: readonly StackedDatum[];
  showLegend?: boolean;
}

function stackText(d: StackedDatum, series: readonly StackSeries[], format: (v: number) => string): string {
  return `${d.label}: ${series.map((s) => `${format(d.values[s.key] ?? 0)} ${s.label.toLowerCase()}`).join(", ")}`;
}

/**
 * Stacked bars (dashboard "Conversations per day": Resolved by AI = `brand`, Handed to staff = `ink`).
 * One focus stop per bar; its name lists every segment.
 */
export function StackedBarChart({ series, data, title, description, axisLabels = [], max, formatValue = defaultFormat, showLegend = true, className }: StackedBarChartProps) {
  const { tip, show, hide } = useChartTooltip();
  const totals = data.map((d) => series.reduce((sum, s) => sum + Math.max(d.values[s.key] ?? 0, 0), 0));
  const top = max ?? niceMax(totals);
  return (
    <div>
      {showLegend ? <ChartLegend className="mb-3" items={series.map((s) => ({ label: s.label, tone: s.tone }))} /> : null}
      <div className={cn("relative", className ?? "h-56")}>
        <SvgFrame title={title} description={description} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full" onMouseLeave={hide}>
          {data.map((d, i) => {
            const s = slot(i, data.length);
            const text = stackText(d, series, formatValue);
            const totalH = ((totals[i] ?? 0) / top) * H;
            const reveal = () => {
              show({ x: (s.center / W) * 100, y: ((H - totalH) / H) * 100, text, index: i });
            };
            let offset = H;
            return (
              <g key={d.label} tabIndex={0} role="img" aria-label={text} className={cn("group", MARK_FOCUS)} onMouseEnter={reveal} onFocus={reveal} onBlur={hide}>
                {series.map((ser) => {
                  const h = (Math.max(d.values[ser.key] ?? 0, 0) / top) * H;
                  offset -= h;
                  return (
                    <rect
                      key={ser.key}
                      x={s.x}
                      y={offset}
                      width={s.width}
                      height={Math.max(h - 1, 0)}
                      vectorEffect="non-scaling-stroke"
                      className={cn(CHART_TONES[ser.tone].fill, "group-hover:opacity-80 group-focus-visible:stroke-fg group-focus-visible:[stroke-width:2px]")}
                    />
                  );
                })}
              </g>
            );
          })}
        </SvgFrame>
        <ChartTooltip tip={tip} />
      </div>
      <AxisLabels labels={axisLabels} />
      <ChartTableFallback
        caption={title}
        table={{ columns: ["", ...series.map((s) => s.label)], rows: data.map((d) => [d.label, ...series.map((s) => formatValue(d.values[s.key] ?? 0))]) }}
      />
    </div>
  );
}
